/**
 * Column-accurate TypeScript types for every table in root `schema.sql`.
 *
 * This file is the frontend's copy of the database contract. API-enriched
 * shapes (joins, omit-secrets, dashboard projections) live in `entities.ts`
 * and `api.ts` and must remain assignable to these row types.
 *
 * Mapping rules (Postgres → TypeScript):
 *  - UUID / INET / INTERVAL          → string
 *  - TIMESTAMPTZ / TIMESTAMP         → ISO-8601 string
 *  - DATE                            → YYYY-MM-DD string
 *  - JSONB                           → Record<string, unknown>
 *  - TEXT[]                          → string[]
 *  - nullable columns                → `T | null` (the API always serialises them)
 */

export type UUID = string;
/** ISO-8601 timestamp with timezone, e.g. `2026-08-24T14:03:00.000Z`. */
export type ISODateTime = string;
/** ISO-8601 calendar date, e.g. `2026-08-24`. */
export type ISODate = string;
/** Postgres INTERVAL as returned by the API (`HH:MM:SS` or ISO-8601 duration). */
export type IntervalString = string;
/** JSONB object. Narrow at the call site when the payload shape is known. */
export type JsonbObject = Record<string, unknown>;

/* ------------------------------------------------------------------ */
/* CHECK-constraint enumerations                                       */
/* ------------------------------------------------------------------ */

export const STATI_CLIENTE = ['attivo', 'sospeso', 'cessato', 'in_attivazione', 'perso'] as const;
export type StatoCliente = (typeof STATI_CLIENTE)[number];
/** @deprecated Prefer `StatoCliente` — kept so existing filters keep compiling. */
export type ClienteStato = StatoCliente;
export const CLIENTE_STATI = STATI_CLIENTE;
/** Main anagraphic list: lost customers live in a dedicated section. */
export const CLIENTE_STATI_OPERATIVI = STATI_CLIENTE.filter((value) => value !== 'perso');
/** Retention window for a lost customer and all related records. */
export const CLIENTE_PERSO_MESI = 6;

export const STATI_CONTRATTO = ['bozza', 'inviato', 'firmato', 'attivo', 'scaduto', 'cessato', 'cancellato'] as const;
export type StatoContratto = (typeof STATI_CONTRATTO)[number];
export type ContrattoStato = StatoContratto;
export const CONTRATTO_STATI = STATI_CONTRATTO;

export const TIPI_CONTRATTO = ['base', 'servizio'] as const;
export type TipoContratto = (typeof TIPI_CONTRATTO)[number];
export const CONTRATTO_TIPI = TIPI_CONTRATTO;

/** List sections: framework agreement, recurring fee, one-off sale. */
export const CONTRATTO_FAMIGLIE = ['quadro', 'canone', 'una_tantum'] as const;
export type ContrattoFamiglia = (typeof CONTRATTO_FAMIGLIE)[number];

export const STATI_FIRMA = ['non_inviato', 'inviato', 'in_firma', 'firmato', 'rifiutato', 'scaduto'] as const;
export type StatoFirma = (typeof STATI_FIRMA)[number];
export type FirmaStato = StatoFirma;
export const FIRMA_STATI = STATI_FIRMA;

export const TIPI_DOCUMENTO_CONTRATTO = [
  'contratto_da_firmare',
  'contratto_firmato',
  'allegato',
  'da_firmare',
  'firmato',
] as const;
export type TipoDocumentoContratto = (typeof TIPI_DOCUMENTO_CONTRATTO)[number];
export const DOCUMENTO_CONTRATTO_TIPI = TIPI_DOCUMENTO_CONTRATTO;

export const STATI_RIGA = ['attivo', 'sospeso', 'scaduto', 'cancellato'] as const;
export type StatoRiga = (typeof STATI_RIGA)[number];
export const RIGA_STATI = STATI_RIGA;

export const RUOLI_UTENTE = [
  'cliente_admin',
  'cliente_user',
  'operatore',
  'operatore_amministrativo',
  'admin',
] as const;
export type RuoloUtente = (typeof RUOLI_UTENTE)[number];
export type Ruolo = RuoloUtente;
/** Schema CHECK lists `cliente_admin` first; the union is order-independent. */
export const RUOLI = RUOLI_UTENTE;

export const STATI_TICKET = ['aperto', 'in_lavorazione', 'risolto', 'chiuso'] as const;
export type StatoTicket = (typeof STATI_TICKET)[number];
export type TicketStato = StatoTicket;
export const TICKET_STATI = STATI_TICKET;

export const PRIORITA_TICKET = ['bassa', 'media', 'alta', 'critica'] as const;
export type PrioritaTicket = (typeof PRIORITA_TICKET)[number];
export type TicketPriorita = PrioritaTicket;
export const TICKET_PRIORITA = PRIORITA_TICKET;

export const STATI_TASK = ['da_fare', 'in_corso', 'in_pausa', 'completato', 'annullato'] as const;
export type StatoTask = (typeof STATI_TASK)[number];
export type TaskStato = StatoTask;
export const TASK_STATI = STATI_TASK;

export const PRIORITA_TASK = ['bassa', 'media', 'alta', 'critica'] as const;
export type PrioritaTask = (typeof PRIORITA_TASK)[number];
export type TaskPriorita = PrioritaTask;
export const TASK_PRIORITA = PRIORITA_TASK;

export const FONTI_TICKET = ['app', 'email', 'telefono', 'alert'] as const;
export type FonteTicket = (typeof FONTI_TICKET)[number];
export type TicketFonte = FonteTicket;
export const TICKET_FONTI = FONTI_TICKET;

/** Preset resolution times offered when closing a ticket (minutes). Custom positive integers are also valid. */
export const TEMPO_RISOLUZIONE_PRESETS = [30, 60, 90, 120] as const;
export type TempoRisoluzionePreset = (typeof TEMPO_RISOLUZIONE_PRESETS)[number];

export const SEVERITY_EVENTO = ['info', 'warning', 'error', 'critical'] as const;
export type SeverityEvento = (typeof SEVERITY_EVENTO)[number];
export type EventSeverity = SeverityEvento;
export const EVENT_SEVERITIES = SEVERITY_EVENTO;

export const STATI_CEDOLINO = [
  'in_attesa',
  'bozza',
  'in_approvazione',
  'approvato',
  'inviato',
  'pagato',
  'annullato',
] as const;
export type StatoCedolino = (typeof STATI_CEDOLINO)[number];
export const CEDOLINO_STATI = STATI_CEDOLINO;

export const STATI_CEDOLINO_PREVENTIVO = [
  'non_aggregato',
  'in_preventivo',
  'fatturato',
  'annullato',
] as const;
export type StatoCedolinoPreventivo = (typeof STATI_CEDOLINO_PREVENTIVO)[number];
export const CEDOLINO_STATO_PREVENTIVO = STATI_CEDOLINO_PREVENTIVO;

export const TIPI_CEDOLINO_VOCE = [
  'tempo_risoluzione',
  'carburante',
  'chilometraggio',
  'pernottamento',
  'altro',
] as const;
export type TipoCedolinoVoce = (typeof TIPI_CEDOLINO_VOCE)[number];
export const CEDOLINO_VOCE_TIPI = TIPI_CEDOLINO_VOCE;

export const STATI_PREVENTIVO = ['bozza', 'inviato', 'accettato', 'rifiutato', 'scaduto', 'convertito'] as const;
export type StatoPreventivo = (typeof STATI_PREVENTIVO)[number];
export const PREVENTIVO_STATI = STATI_PREVENTIVO;

export const TIPI_PREVENTIVO = ['manuale', 'mensile_ticket'] as const;
export type TipoPreventivo = (typeof TIPI_PREVENTIVO)[number];
export const PREVENTIVO_TIPI = TIPI_PREVENTIVO;

export const STATI_PREVENTIVO_CLIENTE = [
  'da_inviare',
  'inviato',
  'accettato',
  'contestato',
  'fatturato',
  'annullato',
] as const;
export type StatoPreventivoCliente = (typeof STATI_PREVENTIVO_CLIENTE)[number];
export const PREVENTIVO_STATI_CLIENTE = STATI_PREVENTIVO_CLIENTE;

export const DOCUMENTO_FONTI = ['wenkey', 'cliente'] as const;
export type DocumentoFonte = (typeof DOCUMENTO_FONTI)[number];

export const DOCUMENTO_CATEGORIE = [
  'generale',
  'tecnica',
  'contrattuale',
  'procedura',
  'sicurezza',
  'altro',
] as const;
export type DocumentoCategoria = (typeof DOCUMENTO_CATEGORIE)[number];

export const VISIBILITA_WENKEY = ['tutti_staff', 'abilitati', 'solo_admin', 'nascosto'] as const;
export type VisibilitaWenkey = (typeof VISIBILITA_WENKEY)[number];

export const VISIBILITA_CLIENTE = ['tutti_client', 'abilitati', 'solo_admin', 'nascosto'] as const;
export type VisibilitaCliente = (typeof VISIBILITA_CLIENTE)[number];

export const DOCUMENTO_AZIONI_ACCESSO = [
  'visualizza',
  'scarica',
  'modifica',
  'elimina',
  'toggle_visibilita',
  'assegna',
  'revoca',
] as const;
export type DocumentoAzioneAccesso = (typeof DOCUMENTO_AZIONI_ACCESSO)[number];

export const STAFF_ROLES: readonly RuoloUtente[] = ['operatore', 'operatore_amministrativo', 'admin'];
export const TECHNICAL_STAFF_ROLES: readonly RuoloUtente[] = ['operatore', 'admin'];
export const ADMINISTRATIVE_STAFF_ROLES: readonly RuoloUtente[] = ['operatore_amministrativo', 'admin'];
export const TENANT_ROLES: readonly RuoloUtente[] = ['cliente_user', 'cliente_admin'];
/** Dashboard, servizi, report: technical staff and tenant admins. Not `cliente_user` or billing-only staff. */
export const COMPANY_HEALTH_ROLES: readonly RuoloUtente[] = ['cliente_admin', 'operatore', 'admin'];

export function isStaffRole(ruolo: RuoloUtente | null | undefined): boolean {
  return ruolo === 'operatore' || ruolo === 'operatore_amministrativo' || ruolo === 'admin';
}

export function isTechnicalStaffRole(ruolo: RuoloUtente | null | undefined): boolean {
  return ruolo === 'operatore' || ruolo === 'admin';
}

export function isAdministrativeStaffRole(ruolo: RuoloUtente | null | undefined): boolean {
  return ruolo === 'operatore_amministrativo' || ruolo === 'admin';
}

export function isTenantRole(ruolo: RuoloUtente | null | undefined): boolean {
  return ruolo === 'cliente_user' || ruolo === 'cliente_admin';
}

/** Amounts on cedolini and catalog prices: staff only. Hidden from tenant roles. */
export function canViewCedolinoBilling(ruolo: RuoloUtente | null | undefined): boolean {
  return isStaffRole(ruolo);
}

/* ------------------------------------------------------------------ */
/* Tables — names match `schema.sql`                                   */
/* ------------------------------------------------------------------ */

/** `clienti` — tenant root. */
export interface Clienti {
  id: UUID;
  ragione_sociale: string;
  partita_iva: string | null;
  /** Codice destinatario SDI (7 char) for e-invoicing. */
  codice_sdi: string | null;
  email_principale: string | null;
  telefono: string | null;
  indirizzo: string | null;
  dominio_email: string | null;
  stato: StatoCliente;
  tariffa_oraria: number;
  /** Art. 17-ter DPR 633/72 — informational for invoicing. */
  split_payment: boolean;
  costo_orario_senior: number;
  costo_orario_junior: number;
  cedolini_visibili_utenti: boolean;
  /** `partner` is billed by Wenkey. `cliente_finale` is served by a partner. */
  tipo?: 'partner' | 'cliente_finale';
  /** Owning partner when `tipo` is `cliente_finale`. */
  partner_id?: UUID | null;
  eliminato_il: ISODateTime | null;
  eliminato_da: UUID | null;
  stato_precedente: StatoCliente | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export const TIPI_RELAZIONE_ENTITA = ['consulenza', 'fatturazione', 'entrambe', 'subappalto'] as const;
export type TipoRelazioneEntita = (typeof TIPI_RELAZIONE_ENTITA)[number];

/**
 * `entita_collegate` — multi-level related entity under a parent cliente.
 * Billing climbs `paga_fattura` via `entita_padre_id`, else falls back to cliente.
 */
export interface EntitaCollegata {
  id: UUID;
  cliente_id: UUID;
  entita_padre_id: UUID | null;
  ragione_sociale: string;
  partita_iva: string | null;
  email: string | null;
  telefono: string | null;
  indirizzo: string | null;
  riceve_servizi: boolean;
  paga_fattura: boolean;
  tipo_relazione: TipoRelazioneEntita;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `sedi` — physical site under an entità collegata or directly under the cliente. */
export interface Sedi {
  id: UUID;
  cliente_id: UUID;
  entita_collegata_id: UUID | null;
  partner_id?: UUID | null;
  cliente_finale_id?: UUID | null;
  nome: string;
  indirizzo: string | null;
  citta: string | null;
  cap: string | null;
  provincia: string | null;
  note: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export const TIPI_FONTE_CLIENTE = ['nota', 'file', 'link'] as const;
export type TipoFonteCliente = (typeof TIPI_FONTE_CLIENTE)[number];

/** `cliente_fonti` — operator-shared documentation sources on a customer. */
export interface ClienteFonti {
  id: UUID;
  cliente_id: UUID;
  tipo: TipoFonteCliente;
  titolo: string;
  descrizione: string | null;
  contenuto: string | null;
  nome_file: string | null;
  tipo_mime: string | null;
  dimensione: number | null;
  url_esterno: string | null;
  visibilita_wenkey: VisibilitaWenkey;
  visibilita_cliente: VisibilitaCliente;
  creato_da: UUID | null;
  aggiornato_da: UUID | null;
  eliminato_il: ISODateTime | null;
  eliminato_da: UUID | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export const FONTE_CESTINO_GIORNI = 90;

export const AZIONI_FONTE_REVISIONE = ['INSERT', 'UPDATE', 'DELETE', 'RESTORE'] as const;
export type AzioneFonteRevisione = (typeof AZIONI_FONTE_REVISIONE)[number];

/** `cliente_fonti_revisioni` — append-only version history of a documentation source. */
export interface ClienteFontiRevisioni {
  id: UUID;
  fonte_id: UUID;
  cliente_id: UUID;
  versione: number;
  azione: AzioneFonteRevisione;
  snapshot: {
    tipo: TipoFonteCliente;
    titolo: string;
    descrizione: string | null;
    contenuto: string | null;
    nome_file: string | null;
    tipo_mime: string | null;
    dimensione: number | null;
    url_esterno: string | null;
  };
  utente_id: UUID | null;
  creato_il: ISODateTime;
}

/** `prodotti` — sellable goods (hardware, licenses). No operational module. */
export interface Prodotti {
  id: UUID;
  codice: string;
  nome: string;
  descrizione: string | null;
  modulo: string | null;
  gestito: boolean;
  attivo: boolean;
  /** List price in EUR. Null when not set. */
  prezzo: number | null;
}

/** `servizi` — ongoing deliverables. `modulo` drives the hub widget. */
export interface Servizi {
  id: UUID;
  codice: string;
  nome: string;
  descrizione: string | null;
  modulo: string | null;
  gestito: boolean;
  attivo: boolean;
  prezzo: number | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `contratti` — legal agreement (testata) with a customer. */
export interface Contratti {
  id: UUID;
  cliente_id: UUID;
  numero_contratto: string | null;
  tipo: TipoContratto;
  data_inizio: ISODate;
  data_fine: ISODate | null;
  gestito: boolean;
  stato: StatoContratto;
  note: string | null;
  firma_stato: StatoFirma;
  firma_data: ISODateTime | null;
  firma_provider: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  eliminato_il?: ISODateTime | null;
  eliminato_da?: UUID | null;
}

/** `contratto_righe` — one prodotto XOR one servizio. */
export interface ContrattoRighe {
  id: UUID;
  contratto_id: UUID;
  prodotto_id: UUID | null;
  servizio_id: UUID | null;
  gestito: boolean;
  data_inizio: ISODate;
  data_fine: ISODate;
  stato: StatoRiga;
  note: string | null;
  /** Contracted price in EUR. Defaults from the catalog item. */
  prezzo: number | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `documenti_contratto` — signed PDF of the agreement. */
export interface DocumentiContratto {
  id: UUID;
  contratto_id: UUID;
  nome_file: string;
  tipo_mime: string | null;
  dimensione: number | null;
  firmato: boolean;
  tipo: TipoDocumentoContratto;
  firma_metadati: JsonbObject | null;
  caricato_il: ISODateTime;
  caricato_da: UUID | null;
}

/** `firma_richieste` — remote signature request. Operator-only at rest; public fields via GET /firma-stato. */
export interface FirmaRichieste {
  id: UUID;
  contratto_id: UUID;
  documento_id: UUID | null;
  provider: string;
  stato: string;
  provider_request_id: string | null;
  url_firma: string | null;
  scadenza: ISODateTime | null;
  creato_il: ISODateTime;
  completato_il: ISODateTime | null;
}

/** `macchina_contratto` — composite PK `(macchina_id, contratto_riga_id)`. */
export interface MacchinaContratto {
  macchina_id: UUID;
  contratto_riga_id: UUID;
  contratto_id: UUID;
  cliente_id: UUID;
  attivato_il: ISODateTime;
  disattivato_il: ISODateTime | null;
}

/** `macchine` — a monitored host. `indirizzo_ip` is INET. */
export interface Macchine {
  id: UUID;
  cliente_id: UUID;
  sede_id: UUID | null;
  nome: string;
  tipo: string | null;
  indirizzo_ip: string | null;
  sistema_operativo: string | null;
  hostname: string | null;
  marca: string | null;
  modello: string | null;
  numero_seriale: string | null;
  cpu: string | null;
  ram_gb: number | null;
  disco: string | null;
  is_appliance: boolean;
  note: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/**
 * `utenti` — includes `password_hash`. The API never returns that column
 * (`utenti_safe` view); use `UtentiSafe` for every response type.
 */
export interface Utenti {
  id: UUID;
  cliente_id: UUID | null;
  nome: string;
  email: string;
  telefono: string;
  ruolo: RuoloUtente;
  password_hash: string | null;
  /** Billing grade for internal operators; client roles stay junior. */
  seniority: 'senior' | 'junior';
  attivo: boolean;
  email_verificata: boolean;
  tentativi_login_falliti?: number;
  bloccato_fino?: ISODateTime | null;
  mfa_abilitato?: boolean;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `utenti_safe` — same row without `password_hash`. */
export type UtentiSafe = Omit<Utenti, 'password_hash'>;

/** `api_keys` — `chiave_hash` is never returned after creation. */
export interface ApiKeys {
  id: UUID;
  cliente_id: UUID | null;
  nome: string;
  chiave_hash: string;
  permessi: string[];
  attiva: boolean;
  globale: boolean;
  scadenza: ISODateTime | null;
  ultimo_uso: ISODateTime | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `sla_policies` — one row per ticket priority. */
export interface SlaPolicies {
  id: UUID;
  nome: string;
  priorita: PrioritaTicket;
  tempo_risposta: IntervalString | null;
  tempo_risoluzione: IntervalString | null;
  attivo: boolean;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `ticket_categorie` lookup. */
export interface TicketCategorie {
  id: UUID;
  codice: string;
  nome: string;
  descrizione: string | null;
  attiva: boolean;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `operatore_categorie` — composite PK `(utente_id, categoria_id)`. */
export interface OperatoreCategorie {
  utente_id: UUID;
  categoria_id: UUID;
  abilitato: boolean;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** `ticket` */
export interface Ticket {
  id: UUID;
  cliente_id: UUID;
  /** Billing partner. */
  partner_id?: UUID;
  cliente_finale_id?: UUID | null;
  macchina_id: UUID | null;
  sede_id: UUID | null;
  contratto_id: UUID | null;
  oggetto: string;
  descrizione: string | null;
  stato: StatoTicket;
  priorita: PrioritaTicket;
  fonte: FonteTicket;
  /** Denormalised `ticket_categorie.nome` for search/reporting. */
  categoria: string | null;
  categoria_id: UUID | null;
  assegnato_a: UUID | null;
  created_by: UUID | null;
  tempo_risoluzione: number | null;
  sla_due_date: ISODateTime | null;
  sla_breached: boolean;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  /** First transition into risolto/chiuso; null while open. */
  closed_at?: ISODateTime | null;
  /** Set when staff move the ticket to the 90-day bin. */
  eliminato_il?: ISODateTime | null;
  eliminato_da?: UUID | null;
}

/** `tasks` — shared operator work items. Never returned to client users. */
export interface Tasks {
  id: UUID;
  /** Null for internal work that is not tied to a customer. */
  cliente_id: UUID | null;
  /** Set when the activity was opened from a lead. */
  lead_id?: UUID | null;
  /** Same lookup as `ticket.categoria_id`. */
  categoria_id: UUID;
  ticket_id: UUID | null;
  entita_collegata_id: UUID | null;
  sede_id: UUID | null;
  titolo: string;
  descrizione: string | null;
  stato: StatoTask;
  priorita: PrioritaTask;
  motivo_pausa: string | null;
  data_scadenza: ISODateTime | null;
  created_by: UUID;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  completed_at: ISODateTime | null;
  /** Set when staff move the activity to the 90-day bin. */
  eliminato_il?: ISODateTime | null;
  eliminato_da?: UUID | null;
}

/** `task_assegnatari` — composite PK `(task_id, utente_id)`. */
export interface TaskAssegnatari {
  task_id: UUID;
  utente_id: UUID;
  assegnato_il: ISODateTime;
}

/** `task_commenti` — activity thread on a task. */
export interface TaskCommenti {
  id: UUID;
  task_id: UUID;
  utente_id: UUID;
  corpo: string;
  creato_il: ISODateTime;
}

/** `ticket_commenti` */
export interface TicketCommenti {
  id: UUID;
  ticket_id: UUID;
  cliente_id: UUID;
  utente_id: UUID | null;
  corpo: string;
  /** Staff-only note; never returned to tenant roles. */
  interno: boolean;
  creato_il: ISODateTime;
}

/** `ticket_stakeholders` — composite PK `(ticket_id, utente_id)`. */
export interface TicketStakeholders {
  ticket_id: UUID;
  utente_id: UUID;
  cliente_id: UUID;
  created_at: ISODateTime;
}

/** `ticket_allegati` */
export interface TicketAllegati {
  id: UUID;
  ticket_id: UUID;
  cliente_id: UUID;
  nome_file: string;
  tipo_mime: string | null;
  dimensione: number | null;
  url_object: string | null;
  creato_il: ISODateTime;
}

/** `notifiche` — `cliente_id` is NULL for notifications addressed to staff. */
export interface Notifiche {
  id: UUID;
  utente_id: UUID;
  cliente_id: UUID | null;
  tipo: string;
  titolo: string | null;
  messaggio: string | null;
  letta: boolean;
  creato_il: ISODateTime;
  letta_il: ISODateTime | null;
}

/** `backup_jobs` */
export interface BackupJobs {
  id: UUID;
  cliente_id: UUID;
  macchina_id: UUID;
  contratto_id: UUID | null;
  job_name: string | null;
  job_status: string | null;
  session_date: ISODateTime | null;
  summary_stats: JsonbObject | null;
  alerts: unknown;
  raw_subject: string | null;
  raw_body: string | null;
  external_message_id: string | null;
  tipo: 'vm_backup' | 'configuration_backup';
  hint_macchina_nome: string | null;
  created_at: ISODateTime;
}

/** `service_events` */
export interface ServiceEvents {
  id: UUID;
  cliente_id: UUID;
  macchina_id: UUID | null;
  contratto_id: UUID | null;
  event_type: string;
  source: string | null;
  severity: SeverityEvento | null;
  payload: JsonbObject;
  event_time: ISODateTime;
  created_at: ISODateTime;
}

/** `macchina_servizio_stato` — composite PK `(macchina_id, contratto_id)`. */
export interface MacchinaServizioStato {
  macchina_id: UUID;
  contratto_id: UUID;
  cliente_id: UUID;
  ultimo_esito: string | null;
  ultimo_aggiornamento: ISODateTime;
  dettagli: JsonbObject | null;
  created_at: ISODateTime;
}

/** `audit_log` */
export interface AuditLog {
  id: UUID;
  tabella: string;
  record_id: UUID;
  cliente_id: UUID | null;
  utente_id: UUID | null;
  azione: string;
  dati_precedenti: JsonbObject | null;
  dati_nuovi: JsonbObject | null;
  creato_il: ISODateTime;
}

/** `cedolini` — administrative debit notes. */
export interface Cedolini {
  id: UUID;
  cliente_id: UUID;
  /** Paying linked entity when billing is not on the parent cliente. */
  entita_pagante_id: UUID | null;
  numero: string;
  data_emissione: ISODate;
  stato: StatoCedolino;
  /** Billing month YYYY-MM from ticket closure. */
  periodo_mese?: string | null;
  preventivo_id?: UUID | null;
  stato_preventivo?: StatoCedolinoPreventivo;
  note: string | null;
  created_by: UUID;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  approved_by: UUID | null;
  approved_at: ISODateTime | null;
}

/** `cedolino_voci` — line items. */
export interface CedolinoVoci {
  id: UUID;
  cedolino_id: UUID;
  tipo: TipoCedolinoVoce;
  descrizione: string | null;
  importo: number;
  quantita: number | null;
  /** Derived EUR/h for tempo_risoluzione (importo / quantita). */
  tariffa_oraria?: number | null;
  ticket_id: UUID | null;
  utente_id: UUID | null;
  riferimento_data: ISODate | null;
  created_at: ISODateTime;
}

/** `cedolino_allegati` — receipts / supporting files for a voce. */
export interface CedolinoAllegati {
  id: UUID;
  cedolino_id: UUID;
  voce_id: UUID;
  cliente_id: UUID;
  nome_file: string;
  tipo_mime: string | null;
  dimensione: number | null;
  url_object: string | null;
  creato_il: ISODateTime;
}

/* ------------------------------------------------------------------ */
/* Singular aliases — the rest of the app uses these names             */
/* ------------------------------------------------------------------ */

export type Cliente = Clienti;
export type Sede = Sedi;
export type ClienteFonteRow = ClienteFonti;
export type Prodotto = Prodotti;
export type Servizio = Servizi;
export type Contratto = Contratti;
export type ContrattoRiga = ContrattoRighe;
export type DocumentoContratto = DocumentiContratto;
export type FirmaRichiesta = FirmaRichieste;
export type Macchina = Macchine;
export type UtenteRow = Utenti;
export type ApiKeyRow = ApiKeys;
export type SlaPolicy = SlaPolicies;
export type TicketCommentoRow = TicketCommenti;
export type TicketAllegatoRow = TicketAllegati;
export type TicketStakeholderRow = TicketStakeholders;
export type Task = Tasks;
export type TaskAssegnatario = TaskAssegnatari;
export type TaskCommentoRow = TaskCommenti;
export type Notifica = Notifiche;
export type BackupJobRow = BackupJobs;
export type ServiceEventRow = ServiceEvents;
export type AuditLogRow = AuditLog;
/** `preventivi` — commercial quotes. Not fiscal invoices. */
export interface Preventivi {
  id: UUID;
  cliente_id: UUID | null;
  lead_id?: UUID | null;
  numero: string;
  anno: number;
  data_emissione: ISODate;
  data_validita: ISODate;
  stato: StatoPreventivo;
  tipo?: TipoPreventivo;
  periodo_mese?: string | null;
  totale_ore?: number;
  n_ticket?: number;
  stato_cliente?: StatoPreventivoCliente;
  data_invio?: ISODateTime | null;
  data_accettazione?: ISODateTime | null;
  valuta: string;
  imponibile: number;
  iva: number;
  totale: number;
  aliquota_iva: number;
  causale: string | null;
  condizioni_pagamento: string;
  modalita_pagamento: string;
  giorni_pagamento: number;
  iban: string | null;
  note: string | null;
  pdf_url: string | null;
  created_by: UUID;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  eliminato_il?: ISODateTime | null;
  eliminato_da?: UUID | null;
}

/** `preventivo_righe` — snapshotted quote lines. */
export interface PreventivoRighe {
  id: UUID;
  preventivo_id: UUID;
  sezione_id?: UUID | null;
  numero_linea: number;
  descrizione: string;
  quantita: number | null;
  unita_misura: string | null;
  prezzo_unitario: number | null;
  prezzo_totale: number;
  aliquota_iva: number;
  cedolino_id: UUID | null;
  cedolino_voce_id: UUID | null;
  ticket_id: UUID | null;
  note: string | null;
}

/** `preventivo_cedolini` — one cedolino belongs to at most one quote. */
export interface PreventivoCedolini {
  preventivo_id: UUID;
  cedolino_id: UUID;
}

/** `documenti` — tenant documents with Wenkey/client visibility toggles. */
export interface Documenti {
  id: UUID;
  cliente_id: UUID;
  /** Set when the document belongs to a cliente_finale (RLS entity_visible). */
  cliente_finale_id?: UUID | null;
  titolo: string;
  descrizione: string | null;
  tipo_mime: string | null;
  dimensione: number | null;
  hash_sha256: string | null;
  nome_file: string | null;
  fonte: DocumentoFonte;
  categoria: DocumentoCategoria;
  visibilita_wenkey: VisibilitaWenkey;
  visibilita_cliente: VisibilitaCliente;
  versione: number;
  documento_padre_id: UUID | null;
  is_corrente: boolean;
  tags: string[] | null;
  caricato_da: UUID;
  eliminato_il: ISODateTime | null;
  eliminato_da: UUID | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface DocumentoAssegnazioni {
  id: UUID;
  documento_id: UUID;
  utente_id: UUID;
  puo_scaricare: boolean;
  scadenza: ISODateTime | null;
  assegnato_da: UUID;
  assegnato_il: ISODateTime;
}

export interface DocumentoAccessi {
  id: UUID;
  documento_id: UUID;
  utente_id: UUID | null;
  cliente_id: UUID;
  azione: DocumentoAzioneAccesso;
  dettagli: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: ISODateTime;
}

export type CedolinoRow = Cedolini;
export type CedolinoVoceRow = CedolinoVoci;
export type CedolinoAllegatoRow = CedolinoAllegati;
export type PreventivoRow = Preventivi;
export type PreventivoRigaRow = PreventivoRighe;
export type DocumentoRow = Documenti;
export type DocumentoAssegnazioneRow = DocumentoAssegnazioni;
export type DocumentoAccessoRow = DocumentoAccessi;
